import React, { useState, useEffect, useRef } from "react";
import { T } from "../lib/theme";
import { lookupPlateLive, lookupPlate, lookupVIN } from "../lib/vehicleLookup";
import { Spin } from "./Atoms";

const SUCCESS_HOLD_MS = 900; // how long "Found: <VIN>" stays visible before auto-advancing — long enough to register as confirmation, short enough not to feel like a stall

export function ScannerModal({onResult,onClose,onNoVin,mode}){
  // Barcode scanning (ZXing) was removed entirely in an earlier pass —
  // unreliable on a real device across two prior fix attempts (no
  // visible feedback, then confirmed dead camera detection). This pass
  // replaces the old "take a photo, then type it in yourself anyway"
  // photo flow with an actual read: the captured photo is sent to
  // Claude's vision via POST /api/vin-from-photo, which locates and
  // extracts a VIN from wherever it's visible — not limited to the OEM
  // VIN plate specifically.
  const[tab,setTab]=useState("manual");
  const[val,setVal]=useState("");
  const[st,setSt]=useState("TX");
  const[found,setFound]=useState(null);
  const[err,setErr]=useState("");
  const[busy,setBusy]=useState(false);
  const[photoData,setPhotoData]=useState(null);
  const[readingVin,setReadingVin]=useState(false); // API call in flight — the visible "Reading VIN..." state, no silent waiting
  const[vinPhotoFound,setVinPhotoFound]=useState(false); // brief confirmation window after a successful read, before auto-advancing to the VIN tab
  const[vinPhotoError,setVinPhotoError]=useState(""); // not-found / cap-hit / network failure — always offers Retake or Enter Manually, never a dead end
  // "photo"|"photo_manual"|null — which capture path (if any) led to the
  // manual VIN tab, and whether it actually found something: "photo"
  // means the field really was auto-filled from the photo (confirm,
  // don't retype); "photo_manual" means the photo read failed and this
  // is the fallback the failure path leads to — same banner mechanism,
  // different message, so landing here after either outcome explains
  // itself instead of looking like an unexplained reset to a blank form.
  const[entrySource,setEntrySource]=useState(null);
  const videoRef=useRef(null);
  const streamRef=useRef(null);
  const canvasRef=useRef(null);
  const vinInputRef=useRef(null);
  const foundTimeoutRef=useRef(null);

  const stopCamera=()=>{
    if(streamRef.current){streamRef.current.getTracks().forEach(t=>t.stop());}
    streamRef.current=null;
    if(foundTimeoutRef.current){clearTimeout(foundTimeoutRef.current);foundTimeoutRef.current=null;}
  };

  const takePhoto=()=>{
    if(!videoRef.current||!canvasRef.current)return;
    const c=canvasRef.current;
    c.width=videoRef.current.videoWidth;
    c.height=videoRef.current.videoHeight;
    c.getContext("2d").drawImage(videoRef.current,0,0);
    const dataUrl=c.toDataURL("image/jpeg",0.8);
    setPhotoData(dataUrl);
    submitPhotoForVin(dataUrl);
  };

  // Fires automatically right after capture — no separate "confirm
  // this photo" step, since a bad photo is cheap to retry and the
  // whole point is not making the user do extra work the read can do
  // itself. Cap-hit, not-found, and network failure all land in the
  // same vinPhotoError state; the UI always offers Retake or Enter
  // Manually from there, never a dead end.
  const submitPhotoForVin=async(dataUrl)=>{
    setReadingVin(true);setVinPhotoError("");setErr("");
    try{
      const base64=(dataUrl||"").split(",")[1]||"";
      const res=await fetch("/api/vin-from-photo",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({image:base64,mediaType:"image/jpeg"})});
      const body=await res.json().catch(()=>({}));
      if(!res.ok){
        setVinPhotoError(body.error||"Could not read the photo.");
      }else if(body.found&&body.vin){
        setVal(body.vin);
        setVinPhotoFound(true);
        foundTimeoutRef.current=setTimeout(()=>{
          setVinPhotoFound(false);
          setEntrySource("photo");
          setTab("manual");
        },SUCCESS_HOLD_MS);
      }else{
        setVinPhotoError("Couldn't read a clear VIN from that photo.");
      }
    }catch(e){
      setVinPhotoError("Connection error — try again.");
    }
    setReadingVin(false);
  };

  const startPhotoStream=async()=>{
    setErr("");
    try{
      const stream=await navigator.mediaDevices.getUserMedia({video:{facingMode:"environment"}});
      streamRef.current=stream;
      if(videoRef.current){videoRef.current.srcObject=stream;videoRef.current.play();}
    }catch(e){setErr("Camera not available.");}
  };

  useEffect(()=>{
    if(tab==="photo"){startPhotoStream();}
    else{stopCamera();}
    return stopCamera;
  },[tab]);

  // Focuses the VIN input on every transition into "manual" — a ref +
  // effect, not the autoFocus attribute, because autoFocus only fires
  // when the input's DOM node is first created, and this component's
  // manual-tab input persists across some transitions (not recreated),
  // so autoFocus alone would silently miss those.
  useEffect(()=>{
    if(tab==="manual")vinInputRef.current?.focus();
  },[tab]);

  const lookup=async()=>{
    if(!val.trim()){setErr("Please enter a value.");return;}
    setErr("");setBusy(true);
    try{
      const res=tab==="plate"?(mode==="live"?await lookupPlateLive(val,st):await lookupPlate(val,st)):await lookupVIN(val,mode);
      setFound({...res,rawValue:val});
    }catch(e){setErr(e.message||"Lookup failed.");}
    setBusy(false);
  };

  // Clearing photoData swaps the JSX from <img> back to <video> at the
  // same position — a different element type, so React unmounts the
  // old node and mounts a fresh one. The stream-attaching effect below
  // only re-fires on a `tab` change, not on `photoData` alone, so the
  // fresh <video> would otherwise show nothing: re-attach the still-live
  // stream (never stopped between capture and retake) directly, rather
  // than re-requesting camera permission from scratch.
  const resetPhotoState=()=>{
    setPhotoData(null);setVinPhotoError("");setReadingVin(false);setVinPhotoFound(false);
    requestAnimationFrame(()=>{
      if(videoRef.current&&streamRef.current){videoRef.current.srcObject=streamRef.current;videoRef.current.play();}
    });
  };

  const inp={width:"100%",border:`1.5px solid ${T.border}`,borderRadius:9,padding:"9px 12px",fontSize:14,color:T.text,background:T.cardAlt,outline:"none"};
  const tabs=["photo","manual","plate"];
  const tabLabels={photo:"Photo",manual:"VIN",plate:"Plate"};

  return(
    <div className="hf-modal-wrap" style={{zIndex:1100}} onClick={e=>{if(e.target===e.currentTarget){stopCamera();onClose();}}}>
      <div className="fade-up hf-scanner-modal" style={{border:`1px solid ${T.border}`}}>
        <div style={{padding:"16px 20px 12px",borderBottom:`1px solid ${T.border}`}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <div>
              <div style={{fontSize:16,fontWeight:800,color:T.text}}>Add Vehicle</div>
              <div style={{fontSize:11,color:mode==="live"?T.green:T.amber,fontWeight:600}}>{mode==="live"?"Live NHTSA":"Demo mode"}</div>
            </div>
            <button onClick={()=>{stopCamera();onClose();}} style={{background:T.slateL,border:`1px solid ${T.border}`,borderRadius:7,padding:"5px 10px",fontSize:13,color:T.textMd}}>✕</button>
          </div>
          <div style={{display:"flex",gap:0,borderRadius:9,overflow:"hidden",border:`1px solid ${T.border}`}}>
            {tabs.map(id=><button key={id} onClick={()=>{setFound(null);setErr("");setEntrySource(null);resetPhotoState();setTab(id);}} style={{flex:1,padding:"8px 0",fontSize:12,fontWeight:600,border:"none",background:tab===id?T.tealL:"transparent",color:tab===id?T.teal:T.textMd,cursor:"pointer"}}>{tabLabels[id]}</button>)}
          </div>
        </div>
        <div style={{padding:"16px 20px",overflowY:"auto",flex:1}}>
          {/* PHOTO TAB */}
          {tab==="photo"&&!found&&(
            <div>
              {vinPhotoFound?(
                // Deliberate, visible confirmation moment — not a silent
                // jump straight to the VIN tab. Same checkmark language
                // as the final "Vehicle Found!" result panel below, so
                // it reads as consistent with the rest of this modal.
                <div className="slide-up" style={{padding:"36px 16px",textAlign:"center"}}>
                  <div style={{width:48,height:48,borderRadius:"50%",background:T.greenL,border:`1px solid ${T.greenBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,margin:"0 auto 12px"}}>✓</div>
                  <div style={{fontSize:15,fontWeight:800,color:T.green,marginBottom:4}}>Found: {val}</div>
                  <div style={{fontSize:12,color:T.textSm}}>Confirm below</div>
                </div>
              ):(
                <>
                  <div style={{position:"relative",borderRadius:10,overflow:"hidden",background:"#000",marginBottom:10,minHeight:180}}>
                    {!photoData?<video ref={videoRef} style={{width:"100%",display:"block",maxHeight:220}} muted playsInline/>:<img src={photoData} style={{width:"100%",borderRadius:8,opacity:readingVin?.5:1}} alt="Captured"/>}
                    <canvas ref={canvasRef} style={{display:"none"}}/>
                  </div>
                  {readingVin?(
                    <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontSize:12,color:T.textMd,fontWeight:600,marginBottom:8,padding:"10px 0"}}>
                      <Spin sz={13}/>
                      <span>Reading VIN...</span>
                    </div>
                  ):vinPhotoError?(
                    <>
                      <div style={{fontSize:13,color:T.red,background:T.redL,borderRadius:8,padding:"9px 14px",marginBottom:8}}>{vinPhotoError}</div>
                      <div style={{display:"flex",gap:8,marginBottom:8}}>
                        <button onClick={resetPhotoState} style={{flex:1,background:T.slateL,color:T.textMd,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px",fontSize:12,fontWeight:600}}>Retake</button>
                        <button onClick={()=>{stopCamera();setEntrySource("photo_manual");setTab("manual");}} style={{flex:1,background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:9,padding:"9px",fontSize:13,fontWeight:700}}>Enter Manually</button>
                      </div>
                    </>
                  ):!photoData&&(
                    <button onClick={takePhoto} style={{width:"100%",background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:9,padding:"10px",fontSize:13,fontWeight:700,marginBottom:8}}>📷 Capture VIN</button>
                  )}
                  <div style={{fontSize:11,color:T.textSm,textAlign:"center"}}>Point the camera at the VIN — dashboard through the windshield, door jamb sticker, or registration paperwork all work</div>
                </>
              )}
            </div>
          )}
          {/* MANUAL VIN TAB */}
          {tab==="manual"&&!found&&(
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {entrySource==="photo"&&<div style={{fontSize:12,color:T.green,background:T.greenL,borderRadius:8,padding:"9px 12px",fontWeight:600}}>✓ Found via photo — confirm the VIN below.</div>}
              {entrySource==="photo_manual"&&<div style={{fontSize:12,color:T.teal,background:T.tealL,borderRadius:8,padding:"9px 12px",fontWeight:600}}>Couldn't read that photo — type the VIN below instead.</div>}
              <div>
                <label style={{fontSize:12,fontWeight:600,color:T.textMd,display:"block",marginBottom:4}}>VIN Number</label>
                <input ref={vinInputRef} value={val} onChange={e=>setVal(e.target.value.toUpperCase())} placeholder="1FTFW1ET4LFA00001" style={{...inp,fontFamily:"'DM Mono',monospace"}}/>
              </div>
              {err&&<div style={{fontSize:13,color:T.red,background:T.redL,borderRadius:8,padding:"9px 14px"}}>{err}</div>}
              <button onClick={lookup} disabled={busy} style={{background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:10,padding:"11px 0",fontSize:14,fontWeight:700,opacity:busy?.6:1}}>{busy?"Looking up...":"Submit"}</button>
              {onNoVin&&<button onClick={()=>{stopCamera();onNoVin();}} style={{background:"none",color:T.textSm,border:"none",fontSize:12,fontWeight:600,padding:0,textAlign:"center"}}>I don't have my VIN</button>}
            </div>
          )}
          {/* PLATE TAB */}
          {tab==="plate"&&!found&&(
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{fontSize:12,color:T.textSm,background:T.slateL,borderRadius:8,padding:"9px 12px"}}>{mode==="live"?"Live mode: plate is resolved to a VIN, then decoded via NHTSA — same accuracy as VIN entry.":"Demo mode uses sample plates only. Switch to Live for real plate lookups."}</div>
              <div style={{display:"grid",gridTemplateColumns:"80px 1fr",gap:10}}>
                <div>
                  <label style={{fontSize:12,fontWeight:600,color:T.textMd,display:"block",marginBottom:4}}>State</label>
                  <select value={st} onChange={e=>setSt(e.target.value)} style={{...inp,padding:"9px 6px"}}>
                    {["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"].map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:12,fontWeight:600,color:T.textMd,display:"block",marginBottom:4}}>Plate Number</label>
                  <input value={val} onChange={e=>setVal(e.target.value.toUpperCase())} onKeyDown={e=>e.key==="Enter"&&lookup()} placeholder="KJW4829" style={inp}/>
                </div>
              </div>
              {err&&<div style={{fontSize:13,color:T.red,background:T.redL,borderRadius:8,padding:"9px 14px"}}>{err}</div>}
              <button onClick={lookup} disabled={busy} style={{background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:10,padding:"11px 0",fontSize:14,fontWeight:700,opacity:busy?.6:1}}>{busy?"Looking up...":"Look Up Plate"}</button>
            </div>
          )}
          {/* RESULT */}
          {found&&(
            <div className="slide-up">
              <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:14}}>
                <div style={{width:38,height:38,borderRadius:9,background:T.greenL,border:`1px solid ${T.greenBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>✓</div>
                <div><div style={{fontSize:14,fontWeight:800,color:T.green}}>Vehicle Found!</div><div style={{fontSize:12,color:T.textSm}}>{found.source} · {found.rawValue}</div></div>
              </div>
              <div style={{background:T.slateL,borderRadius:10,border:`1px solid ${T.border}`,padding:"12px 14px",marginBottom:14,display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {/* Engine/Body used to read found.data.engine/found.data.body,
                    which don't exist on ANY of the four lookup response shapes
                    (see src/lib/vehicleLookup.js) — always fell back to "—"
                    even on a real live decode carrying genuinely richer data
                    (bodyClass/displacement/cylinders/driveType/fuelType) under
                    different key names, or a demo lookup's own `color` field.
                    Built from whatever's actually present instead, so nothing
                    real gets silently dropped. */}
                {[
                  ["Year",found.data.year],
                  ["Make",found.data.make],
                  ["Model",found.data.model],
                  ["Trim",found.data.trim||"—"],
                  ...(found.data.bodyClass?[["Body",found.data.bodyClass]]:[]),
                  ...([found.data.displacement,found.data.cylinders?`${found.data.cylinders}-cyl`:null].filter(Boolean).join(", ")?[["Engine",[found.data.displacement,found.data.cylinders?`${found.data.cylinders}-cyl`:null].filter(Boolean).join(", ")]]:[]),
                  ...(found.data.driveType?[["Drivetrain",found.data.driveType]]:[]),
                  ...(found.data.fuelType?[["Fuel Type",found.data.fuelType]]:[]),
                  ...(found.data.color?[["Color",found.data.color]]:[]),
                ].map(([l,v])=>(
                  <div key={l}><div style={{fontSize:10,color:T.textSm,textTransform:"uppercase",letterSpacing:".05em",marginBottom:2}}>{l}</div><div className="mono" style={{fontSize:13,fontWeight:700}}>{v}</div></div>
                ))}
              </div>
              <button onClick={()=>onResult({...found.data,vin:tab!=="plate"?found.rawValue:found.data.vin})} style={{width:"100%",background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:10,padding:"11px 0",fontSize:14,fontWeight:700}}>Add to Fleet →</button>
            </div>
          )}
          {/* Always reachable, not tucked below anything — the explicit
              escape hatch into the full manual form, per this session's
              Part 3. */}
          {!found&&<button onClick={()=>{stopCamera();onClose();}} style={{width:"100%",background:"none",color:T.textSm,border:"none",fontSize:12,fontWeight:600,marginTop:14,padding:0,textAlign:"center"}}>Skip lookup, enter details manually →</button>}
        </div>
      </div>
    </div>
  );
}
